import {
  formatAmount,
  useInvoiceRecipientAddress,
  HKInvoiceFooter,
  HKInvoiceHeader,
  InvoiceInfoBlock,
  HKInvoiceItemsTable,
  InvoiceLayout,
  OrderDetailBatchReturnType,
  TransferProductCost,
} from './common';

const HKInvoice = ({
  order,
  transferCostBySourceProductId,
}: {
  order: OrderDetailBatchReturnType;
  transferCostBySourceProductId?: Record<number, TransferProductCost>;
}) => {
  const subTotal = order.payAmountActualCurrency || 0;
  const actualCurrencyRate = order.orderItems[0]?.actualCurrencyRate ?? 1;
  const integrationDeduction = order.useIntegration
    ? Math.round(order.useIntegration * actualCurrencyRate)
    : 0;
  const summaryRows = [
    order.freightAmountActualCurrency
      ? {
          label: 'Freight :',
          value: formatAmount(order.freightAmountActualCurrency),
        }
      : null,
    order.promotionAmountActualCurrency
      ? {
          label: 'Discount :',
          value: `-${formatAmount(order.promotionAmountActualCurrency)}`,
        }
      : null,
    integrationDeduction
      ? {
          label: 'Points Deduction :',
          value: `-${formatAmount(integrationDeduction)}`,
        }
      : null,
    {
      label: 'Sub Total Amount :',
      value: formatAmount(subTotal),
    },
  ].filter((row): row is { label: string; value: string } => Boolean(row));

  const { cityLabel, countryLabel } = useInvoiceRecipientAddress(order);

  return (
    <InvoiceLayout>
      <HKInvoiceHeader title="SALES INVOICE" />

      <section className="mb-[30px] flex items-start justify-between gap-6">
        <div>
          <div className="font-bold mb-2 text-xs">To :</div>
          <div className="text-xs leading-[1.8]">
            {order.receiverName}
            {order.receiverDetailAddress ? (
              <>
                <br />
                {order.receiverDetailAddress}
              </>
            ) : null}
            {cityLabel || countryLabel ? (
              <>
                <br />
                {cityLabel} {countryLabel}
              </>
            ) : null}
            {order.receiverPhone ? (
              <>
                <br />
                {order.receiverPhone}
              </>
            ) : null}
          </div>
        </div>
        <InvoiceInfoBlock
          order={order}
          invoiceNoLabel="Invoice No :"
          showGstRegistration={false}
          showTerms={false}
        />
      </section>

      <HKInvoiceItemsTable
        order={order}
        transferCostBySourceProductId={transferCostBySourceProductId}
      />

      <section className="ml-auto mr-[6px] w-[330px] text-xs mb-[30px]">
        {summaryRows.map((row) => (
          <div
            className="grid grid-cols-[1fr_112px] gap-x-8 mb-2 last:mb-0"
            key={row.label}
          >
            <span className="text-right whitespace-nowrap">{row.label}</span>
            <span className="text-right tabular-nums">{row.value}</span>
          </div>
        ))}
      </section>

      <p className="text-sm font-bold text-center mb-[34px]">
        ITEMS SOLD CANNOT BE RETURNED OR EXCHANGED
      </p>

      <section className="text-xs leading-[1.8]">
        <HKInvoiceFooter />
      </section>
    </InvoiceLayout>
  );
};

export default HKInvoice;
